// eslint-disable-next-line jira/filename-case
import React, { Component } from 'react';
import withAnalyticsEvents, {
	type WithAnalyticsEventsProps,
} from '@atlaskit/analytics-next/withAnalyticsEvents';
import {
	UI_EVENT_TYPE,
	OPERATIONAL_EVENT_TYPE,
	TRACK_EVENT_TYPE,
	SCREEN_EVENT_TYPE,
	type EventTypes,
} from '../constants.tsx';
import type { AnalyticsEvent } from '../types.tsx';
import { fireAnalytics } from './fire-analytics.tsx';

/**
 * Those rules are not smart enough to detect the use case here
 */
export type AnalyticsProps = {
	componentName?: string;
	eventName?: string;
	action?: string;
	actionSubject?: string;
	actionSubjectId?: string;
	attributes?: {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		[key: string]: any;
	};
};

type Props = {
	createAnalyticsEvent: (arg1: { action: string }) => AnalyticsEvent;
	type: EventTypes;
} & AnalyticsProps;

// eslint-disable-next-line jira/react/no-class-components
class FireComponent extends Component<Props> {
	componentDidMount() {
		const {
			type,
			attributes,
			eventName,
			componentName,
			action: actionProps,
			actionSubject: actionSubjectProps,
			actionSubjectId: actionSubjectIdProps,
		} = this.props;

		if (eventName && (actionProps || actionSubjectProps)) {
			throw Error('eventName is an alias for action & actionSubject, provide only one of those');
		}
		if (componentName && actionSubjectIdProps) {
			throw Error('componentName is an alias for actionSubjectId, provide only one of those');
		}

		const eventNameSplit = eventName ? eventName.split(' ') : [];
		const action = actionProps || eventNameSplit[1];
		const actionSubject = actionSubjectProps || eventNameSplit[0];

		if (!action || !actionSubject) {
			throw Error(
				'eventName should exist and be in the format "subject action", i.e. "button clicked", "menu opened", "issue created", etc',
			);
		}

		const actionSubjectId = actionSubjectIdProps || componentName;

		const analyticsEvent = this.props.createAnalyticsEvent({ action });

		fireAnalytics(analyticsEvent, type, {
			action,
			actionSubject,
			actionSubjectId,
			attributes,
		});
	}

	render() {
		return null;
	}
}

export const WithAnalytics: React.ForwardRefExoticComponent<
	Omit<
		{
			createAnalyticsEvent: (arg1: { action: string }) => AnalyticsEvent;
			type: EventTypes;
		} & AnalyticsProps,
		keyof WithAnalyticsEventsProps
	> &
		React.RefAttributes<unknown>
	// @ts-expect-error - TS2345 - Argument of type 'typeof FireComponent' is not assignable to parameter of type '(((props: WithAnalyticsEventsProps) => ReactElement<any, string | ((props: any) => ReactElement<any, any> | null) | (new (props: any) => Component<any, any, any>)> | null) & typeof FireComponent) | ((new (props: WithAnalyticsEventsProps) => Component<...>) & typeof FireComponent)'.
> = withAnalyticsEvents()(FireComponent);

/**
 * @deprecated - use Screen event for "viewed" events, or fireUiAnalytics() for "clicked" etc. events
 * UI events are usually fired on user interaction, not on render
 * Read https://hello.atlassian.net/wiki/spaces/AAPT/pages/134329336/UI+Events and https://hello.atlassian.net/wiki/spaces/AAPT/pages/134329341/Screen+Events
 */
export const FireUiAnalytics = (props: AnalyticsProps): React.JSX.Element => (
	<WithAnalytics {...props} type={UI_EVENT_TYPE} />
);

/**
 * @deprecated - use Screen event for "viewed" events, or fireTrackAnalytics() on action completion
 * Track events are usually fired on user action competion, not on render
 * Read https://hello.atlassian.net/wiki/spaces/AAPT/pages/134329319/Track+Events and https://hello.atlassian.net/wiki/spaces/AAPT/pages/134329341/Screen+Events
 */
export const FireTrackAnalytics = (props: AnalyticsProps): React.JSX.Element => (
	<WithAnalytics {...props} type={TRACK_EVENT_TYPE} />
);

export const FireOperationalAnalytics = (props: AnalyticsProps): React.JSX.Element => (
	<WithAnalytics {...props} type={OPERATIONAL_EVENT_TYPE} />
);

/**
 * Note: this component is type-unsafe, does not check props. Please fix its @ts-expect-error
 */
export const FireScreenAnalytics = (props: AnalyticsProps): React.JSX.Element => (
	<WithAnalytics {...props} type={SCREEN_EVENT_TYPE} eventName="screen viewed" />
);
